import { stitch } from "../lib/words.js";

/**
 * `VoiceInput` — the seam Tencent ASR (M-13) replaces.
 *
 * For M1 the engine behind it is the browser's Web Speech API, which is a
 * stand-in and is labelled as one in the README. The adapter's contract is
 * three methods and four callbacks: `start` / `stop` / `cancel`, `onTranscript`,
 * `onSegment`, plus state and errors. Nothing here can confirm anything — voice
 * carries zero authority in this product, and there is deliberately no method on
 * this class that reaches the gateway.
 *
 * DCTA-141: the session is the app's, not the engine's. The engine runs
 * continuously; when it stops by itself (iOS does after a pause, Chrome and Android
 * on "no-speech") a NEW engine takes over at once and `onSegment` tells the app a new
 * piece begins, so the end-of-speech module (`app/end-of-speech.ts`) stitches the
 * transcript. Only the app's `stop()` / `cancel()` — its end-of-speech decision — or
 * a real failure ends a session.
 */

export type VoiceState = "unavailable" | "idle" | "listening";

export interface Recognizer {
  start(): void;
  stop(): void;
  /** Drop everything at once (no final result). Engines without it are stopped instead. */
  abort?(): void;
  onresult?: ((transcript: string, final: boolean) => void) | undefined;
  onend?: (() => void) | undefined;
  onerror?: ((code: string) => void) | undefined;
  /** DCTA-181: an engine that holds the microphone itself reports each chunk's loudness (0..1) — the bars follow it. */
  onlevel?: ((level: number) => void) | undefined;
  /** DCTA-181: this engine reports `onlevel` (it holds the microphone). */
  readonly levels?: boolean;
}

export type RecognizerFactory = () => Recognizer;

/**
 * After stop(), an engine that has not ended by then is aborted and the session closed — `lastEnd` "timeout" (DCTA-89).
 * DCTA-181: 8 s, not 2 — Tencent's stable sentence comes after the end frame, and on a slow line seconds after Done;
 * 2 s sent the interim words, or nothing.
 */
export const END_WATCHDOG_MS = 8000;
/** DCTA-141: an engine that ends this soon after its start, having heard nothing, is a dead one. */
export const DUD_MS = 1000;
/** Three dead engines in a row: the restarts are refused — the session ends rather than spin. */
const DUDS = 3;
/** Not failures in a continuous session: the engine's own idea of silence, or its own stop. Its end follows. */
const BENIGN = new Set(["no-speech", "aborted"]);
const REFUSED = new Set(["not-allowed", "service-not-allowed"]);

/**
 * DCTA-89 (voice failed on the second press, Umakhan's iPhone): one session at a time, each engine
 * its OWN instance. The engine that owns the state is `#engine`; an event from any other engine —
 * a finished one reporting "aborted" after its "end", a second "end" — is ignored, so it can never
 * end the next session or reach the app. stop() asks the engine to finish; if it has not ended
 * within END_WATCHDOG_MS it is aborted and the session closed, so a stuck engine cannot swallow
 * the next press. `lastEnd` says how the last session ended (an error code, "end", "cancel",
 * "restart-failed", "restart:<code>") for the device notes.
 */
export class VoiceInput {
  #factory: RecognizerFactory | undefined;
  #engine: Recognizer | undefined;
  /** The app still wants this session: an engine that ends by itself is replaced. */
  #wanted = false;
  /** Engines started in this session (the first one's refusal means the microphone was denied). */
  #engines = 0;
  #duds = 0;
  #watchdog: ReturnType<typeof setTimeout> | undefined;
  #state: VoiceState;
  #lastEnd: string | undefined;
  #transcript: ((text: string, final: boolean) => void) | undefined;
  #levelCb: ((level: number) => void) | undefined;
  #segmentCb: (() => void) | undefined;
  #stateCb: ((state: VoiceState) => void) | undefined;
  #errorCb: ((code: string) => void) | undefined;

  constructor(factory: RecognizerFactory | undefined) {
    this.#factory = factory;
    this.#state = factory === undefined ? "unavailable" : "idle";
  }

  get available(): boolean {
    return this.#state !== "unavailable";
  }

  get state(): VoiceState {
    return this.#state;
  }

  /** DCTA-181: the session's engine reports the microphone's loudness (Tencent's holds the microphone; the browser's does not). */
  get levels(): boolean {
    return this.#engine?.levels === true;
  }

  /** How the last session ended: the engine's error code, "end", "cancel", "timeout", "restart-failed" or "restart:<code>". */
  get lastEnd(): string | undefined {
    return this.#lastEnd;
  }

  /** The current engine's text for the words in progress (each engine starts from nothing). */
  onTranscript(cb: (text: string, final: boolean) => void): void {
    this.#transcript = cb;
  }

  /** DCTA-181: the microphone's loudness, chunk by chunk, from an engine that holds the microphone itself (Tencent's). */
  onLevel(cb: (level: number) => void): void {
    this.#levelCb = cb;
  }

  /** A new engine took over from one that stopped by itself: the next words are a new piece of the same sentence. */
  onSegment(cb: () => void): void {
    this.#segmentCb = cb;
  }

  onState(cb: (state: VoiceState) => void): void {
    this.#stateCb = cb;
  }

  onError(cb: (code: string) => void): void {
    this.#errorCb = cb;
  }

  start(): void {
    if (this.#state !== "idle" || this.#factory === undefined || this.#engine !== undefined) return;
    this.#wanted = true;
    this.#engines = 0;
    this.#duds = 0;
    this.#set("listening");
    this.#spawn();
  }

  /** The app's decision: the engine finishes (its last words still arrive), then the session ends. */
  stop(): void {
    const engine = this.#engine;
    if (engine === undefined) return;
    this.#wanted = false;
    engine.stop();
    if (this.#watchdog !== undefined) return;
    this.#watchdog = setTimeout(() => {
      this.#watchdog = undefined;
      if (this.#engine !== engine) return;
      this.#close("timeout", "idle");
      try {
        (engine.abort ?? engine.stop).call(engine);
      } catch {
        /* an engine that is already gone */
      }
    }, END_WATCHDOG_MS);
  }

  /** Drop the session at once — no last words wanted (Cancel, a tap on the words): nothing late reaches the app. */
  cancel(): void {
    const engine = this.#engine;
    if (engine === undefined) return;
    this.#close("cancel", "idle");
    try {
      (engine.abort ?? engine.stop).call(engine);
    } catch {
      /* an engine that is already gone */
    }
  }

  /** A new engine for the session — a loop, never recursion: one that cannot start is a dead one. */
  #spawn(): void {
    const factory = this.#factory;
    while (this.#wanted && factory !== undefined) {
      const engine = factory();
      const first = this.#engines === 0;
      this.#engines += 1;
      this.#engine = engine;
      const born = Date.now();
      let heard = false;
      const mine = (): boolean => this.#engine === engine;
      engine.onresult = (text, final) => {
        if (!mine()) return;
        heard = true;
        this.#transcript?.(text, final);
      };
      engine.onlevel = (level) => {
        if (mine()) this.#levelCb?.(level);
      };
      engine.onend = () => {
        if (!mine()) return;
        if (!this.#wanted) {
          this.#close("end", "idle");
          return;
        }
        // It stopped by itself: a new engine takes over at once.
        this.#duds = !heard && Date.now() - born < DUD_MS ? this.#duds + 1 : 0;
        if (this.#duds >= DUDS) {
          this.#close("restart-failed", "idle");
          return;
        }
        this.#segmentCb?.();
        this.#spawn();
      };
      engine.onerror = (code) => {
        if (!mine() || BENIGN.has(code)) return;
        if (REFUSED.has(code) && !first) {
          // The first engine had the permission: a refused restart is the platform's, not the customer's.
          this.#close(`restart:${code}`, "idle");
          return;
        }
        // A denied microphone is not a transient failure: the composer shows the
        // designed "Voice unavailable" state and typing keeps working.
        this.#close(code, REFUSED.has(code) ? "unavailable" : "idle");
        this.#errorCb?.(code);
      };
      try {
        engine.start();
        return;
      } catch {
        engine.onresult = undefined;
        engine.onend = undefined;
        engine.onerror = undefined;
        this.#duds += 1;
        if (first || this.#duds >= DUDS) {
          this.#close(first ? "start-failed" : "restart-failed", "idle");
          return;
        }
      }
    }
  }

  #close(how: string, next: VoiceState): void {
    this.#engine = undefined;
    this.#wanted = false;
    if (this.#watchdog !== undefined) clearTimeout(this.#watchdog);
    this.#watchdog = undefined;
    this.#lastEnd = how;
    this.#set(next);
  }

  #set(state: VoiceState): void {
    if (this.#state === state) return;
    this.#state = state;
    this.#stateCb?.(state);
  }
}

/**
 * DCTA-141 × DCTA-142: an engine that can step aside. `primary` (Tencent's — the default since DCTA-162) is tried; when
 * it fails before it heard anything with a code `fallsBack` accepts — "not here": no keys at the bank, a refused signature,
 * the day's cap, a failed socket (`fallsBackToBrowser`) — `fallback` (the browser's) takes the SAME listening at once, so
 * the app never sees the failure, every later listening starts with the fallback (for the page), and `onAside` is told
 * once, with the code (DCTA-162: the app says so, quietly). A failure after words, any other code, a listening the app
 * already stopped, or no fallback at all: the failure is the app's, as it would be without this.
 */
export function withFallback(
  primary: RecognizerFactory,
  fallback: RecognizerFactory | undefined,
  fallsBack: (code: string) => boolean,
  onAside?: (code: string) => void,
): RecognizerFactory {
  let aside = false;
  return () => {
    if (aside && fallback !== undefined) return fallback();
    let current = primary();
    let heard = false;
    let ended = false;
    const outer: Recognizer = {
      get levels() {
        return current.levels === true;
      },
      start: () => current.start(),
      stop: () => {
        ended = true;
        current.stop();
      },
      abort: () => {
        ended = true;
        (current.abort ?? current.stop).call(current);
      },
    };
    const wire = (engine: Recognizer, first: boolean): void => {
      engine.onresult = (text, final) => {
        if (current !== engine) return;
        heard = true;
        outer.onresult?.(text, final);
      };
      engine.onlevel = (level) => {
        if (current === engine) outer.onlevel?.(level);
      };
      engine.onend = () => {
        if (current === engine) outer.onend?.();
      };
      engine.onerror = (code) => {
        if (current !== engine) return;
        if (first && !heard && !ended && fallback !== undefined && fallsBack(code)) {
          aside = true;
          // DCTA-162: once a page — every later listening starts with the fallback — for the app's quiet line.
          onAside?.(code);
          const next = fallback();
          // The primary's late end is ignored from here: it is no longer `current`.
          current = next;
          wire(next, false);
          try {
            next.start();
          } catch {
            outer.onerror?.("start-failed");
            outer.onend?.();
          }
          return;
        }
        outer.onerror?.(code);
      };
    };
    wire(current, true);
    return outer;
  };
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort?(): void;
  onresult: ((event: SpeechResultEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
}

interface SpeechResultEventLike {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: { isFinal: boolean; 0: { transcript: string } };
  };
}

/**
 * The Web Speech engine, when the browser has one. Returns `undefined`
 * otherwise, which is what puts the composer into "Voice unavailable".
 */
export function webSpeechFactory(): RecognizerFactory | undefined {
  const w = globalThis as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  if (Ctor === undefined) return undefined;
  return () => {
    const engine = new Ctor();
    // en-SG so local accents are at least declared to the engine. The brief's
    // "accents" requirement belongs to M-13 and Tencent ASR, not to this.
    engine.lang = "en-SG";
    // DCTA-141: continuous — the engine's own short silence no longer ends the sentence; the app decides
    // (app/end-of-speech.ts). Where the platform still stops it, VoiceInput starts a new one at once.
    engine.continuous = true;
    engine.interimResults = true;
    const recognizer: Recognizer = {
      start: () => engine.start(),
      stop: () => engine.stop(),
      abort: () => (engine.abort ?? engine.stop).call(engine),
    };
    engine.onresult = (event) => {
      // Every result of the session, read as one text: settled results and the one in progress. Stitched, not
      // concatenated — Android restates everything so far in each result ("hello", "hello world"). Final only
      // when every result is settled.
      let text = "";
      let final = event.results.length > 0;
      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result === undefined) continue;
        text = stitch(text, result[0].transcript);
        if (!result.isFinal) final = false;
      }
      recognizer.onresult?.(text, final);
    };
    engine.onend = () => {
      recognizer.onend?.();
      // Nothing of a finished engine stays attached (DCTA-89): no late event reaches the app.
      engine.onresult = null;
      engine.onend = null;
      engine.onerror = null;
    };
    engine.onerror = (event) => recognizer.onerror?.(event.error ?? "unknown");
    return recognizer;
  };
}
